import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const CERTIFICATE_PAGE_WIDTH_PX = 899;
const CERTIFICATE_PAGE_HEIGHT_PX = 1150;
const PDF_DPI = 96;
const MM_PER_INCH = 25.4;
const pxToMm = (px) => (px / PDF_DPI) * MM_PER_INCH;

// Convert oklch color to RGB using canvas
const oklchToRgb = (oklchValue) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = oklchValue;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
  return a < 255 ? `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(3)})` : `rgb(${r}, ${g}, ${b})`;
};

// Convert oklch colors in a cloned document for html2canvas compatibility
const convertOklchInClonedDocument = (clonedDoc) => {
  // Convert oklch in all stylesheets
  Array.from(clonedDoc.styleSheets).forEach((sheet) => {
    try {
      Array.from(sheet.cssRules || []).forEach((rule) => {
        if (rule.style) {
          const cssText = rule.cssText;
          if (cssText.includes('oklch')) {
            // Replace oklch values in the rule
            const oklchRegex = /oklch\([^)]+\)/g;
            const matches = cssText.match(oklchRegex);
            if (matches) {
              matches.forEach((match) => {
                const rgbValue = oklchToRgb(match);
                rule.style.cssText = rule.style.cssText.replace(match, rgbValue);
              });
            }
          }
        }
      });
    } catch (e) {
      // Cross-origin stylesheets will throw, ignore them
    }
  });

  // Convert oklch in inline styles and computed styles
  const allElements = clonedDoc.querySelectorAll('*');
  allElements.forEach((el) => {
    const computedStyle = clonedDoc.defaultView.getComputedStyle(el);
    const colorProps = [
      'color',
      'background-color',
      'border-color',
      'border-top-color',
      'border-right-color',
      'border-bottom-color',
      'border-left-color',
      'outline-color',
    ];

    colorProps.forEach((prop) => {
      const value = computedStyle.getPropertyValue(prop);
      if (value && value.includes('oklch')) {
        const camelProp = prop.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        el.style[camelProp] = oklchToRgb(value);
      }
    });

    // Handle box-shadow separately as it can have multiple values
    const boxShadow = computedStyle.getPropertyValue('box-shadow');
    if (boxShadow && boxShadow.includes('oklch')) {
      el.style.boxShadow = boxShadow.replace(/oklch\([^)]+\)/g, (match) => oklchToRgb(match));
    }
  });
};

export const waitForNextPaint = async () => {
  await new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });

  await new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
};

const addCanvasToPdf = (pdf, canvas, useCompression = false) => {
  const imgData = useCompression
    ? canvas.toDataURL('image/jpeg', 0.95)
    : canvas.toDataURL('image/png');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const paddingHorizontal = pxToMm(10);
  const usableWidth = Math.max(pageWidth - 2 * paddingHorizontal, 0);
  const scale = Math.min(usableWidth / canvas.width, pageHeight / canvas.height);

  const imgScaledWidth = canvas.width * scale;
  const imgScaledHeight = canvas.height * scale;

  const offsetX = (pageWidth - imgScaledWidth) / 2;
  const offsetY = (pageHeight - imgScaledHeight) / 2;

  const format = useCompression ? 'JPEG' : 'PNG';
  pdf.addImage(imgData, format, offsetX, offsetY, imgScaledWidth, imgScaledHeight);
};

const createCertificateCanvas = async (content, { ignoreElements, scale = 1.5 } = {}) => {
  if (!content) {
    return null;
  }

  return html2canvas(content, {
    useCORS: true,
    scale,
    ignoreElements,
    onclone: (clonedDoc) => {
      // Convert oklch colors to RGB in the cloned document for html2canvas compatibility
      convertOklchInClonedDocument(clonedDoc);
    },
  });
};

const addCanvasToPdfSideBySide = (pdf, leftCanvas, rightCanvas, useCompression = true) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const paddingHorizontal = pxToMm(10);
  const paddingVertical = pxToMm(10);
  const centerGap = pxToMm(20);

  const usableWidth = pageWidth - 2 * paddingHorizontal - centerGap;
  const usableHeight = pageHeight - 2 * paddingVertical;
  const halfWidth = usableWidth / 2;

  const leftScale = Math.min(halfWidth / leftCanvas.width, usableHeight / leftCanvas.height);
  const rightScale = Math.min(halfWidth / rightCanvas.width, usableHeight / rightCanvas.height);

  const leftImgWidth = leftCanvas.width * leftScale;
  const leftImgHeight = leftCanvas.height * leftScale;
  const rightImgWidth = rightCanvas.width * rightScale;
  const rightImgHeight = rightCanvas.height * rightScale;

  const leftOffsetX = paddingHorizontal + (halfWidth - leftImgWidth) / 2;
  const leftOffsetY = (pageHeight - leftImgHeight) / 2;

  const rightOffsetX = paddingHorizontal + halfWidth + centerGap + (halfWidth - rightImgWidth) / 2;
  const rightOffsetY = (pageHeight - rightImgHeight) / 2;

  const leftImgData = useCompression
    ? leftCanvas.toDataURL('image/jpeg', 0.95)
    : leftCanvas.toDataURL('image/png');
  const rightImgData = useCompression
    ? rightCanvas.toDataURL('image/jpeg', 0.95)
    : rightCanvas.toDataURL('image/png');

  const format = useCompression ? 'JPEG' : 'PNG';
  pdf.addImage(leftImgData, format, leftOffsetX, leftOffsetY, leftImgWidth, leftImgHeight);
  pdf.addImage(rightImgData, format, rightOffsetX, rightOffsetY, rightImgWidth, rightImgHeight);
};

export const exportCertificatePdfBoth = async ({
  frontElementVi,
  backElementVi,
  frontElementEn,
  backElementEn,
  fileName,
  ignoreElements,
}) => {
  if (!frontElementVi || !backElementVi || !frontElementEn || !backElementEn) {
    return;
  }

  const [frontCanvasVi, backCanvasVi, frontCanvasEn, backCanvasEn] = await Promise.all([
    createCertificateCanvas(frontElementVi, { ignoreElements, scale: 2.0 }),
    createCertificateCanvas(backElementVi, { ignoreElements, scale: 2.0 }),
    createCertificateCanvas(frontElementEn, { ignoreElements, scale: 2.0 }),
    createCertificateCanvas(backElementEn, { ignoreElements, scale: 2.0 }),
  ]);

  if (!frontCanvasVi || !backCanvasVi || !frontCanvasEn || !backCanvasEn) {
    return;
  }

  const pdf = new jsPDF({
    orientation: 'l',
    unit: 'mm',
    format: 'a3',
  });

  addCanvasToPdfSideBySide(pdf, backCanvasEn, frontCanvasEn);

  pdf.addPage('a3', 'l');
  addCanvasToPdfSideBySide(pdf, backCanvasVi, frontCanvasVi);

  const pdfBlob = pdf.output('blob');
  saveAs(pdfBlob, fileName);
};

export const exportCertificatePdf = async ({
  frontElement,
  backElement,
  fileName,
  ignoreElements,
}) => {
  if (!frontElement || !backElement) {
    return;
  }

  const [frontCanvas, backCanvas] = await Promise.all([
    createCertificateCanvas(frontElement, { ignoreElements, scale: 2.0 }),
    createCertificateCanvas(backElement, { ignoreElements, scale: 2.0 }),
  ]);

  if (!frontCanvas || !backCanvas) {
    return;
  }

  const pdf = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: [pxToMm(CERTIFICATE_PAGE_WIDTH_PX), pxToMm(CERTIFICATE_PAGE_HEIGHT_PX)],
  });

  addCanvasToPdf(pdf, frontCanvas, false);
  pdf.addPage([pxToMm(CERTIFICATE_PAGE_WIDTH_PX), pxToMm(CERTIFICATE_PAGE_HEIGHT_PX)], 'p');
  addCanvasToPdf(pdf, backCanvas, false);

  const pdfBlob = pdf.output('blob');
  saveAs(pdfBlob, fileName);
};
